import React from 'react';
import { motion } from 'framer-motion';
import { Mail, Phone, MapPin, Globe, Instagram } from 'lucide-react';
import { CONTACT_INFO } from '../utils/constants';

const Contact = () => {
  const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.8 }
    }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15
      }
    }
  };

  const contactInfo = [
    {
      icon: Phone,
      title: 'Phone',
      details: [CONTACT_INFO.phone],
      link: `tel:${CONTACT_INFO.phone}`
    },
    {
      icon: Mail,
      title: 'Email',
      details: [CONTACT_INFO.email],
      link: `mailto:${CONTACT_INFO.email}`
    },
    {
      icon: Globe,
      title: 'Website',
      details: [CONTACT_INFO.website],
      link: `https://${CONTACT_INFO.website}`
    },
    {
      icon: Instagram,
      title: 'Instagram',
      details: [CONTACT_INFO.instagram],
      link: `https://instagram.com/${CONTACT_INFO.instagram.replace('@', '')}`
    }
  ];

  return (
    <div className="pt-24 min-h-screen bg-accent-cream">
      {/* Hero Section */}
      <section className="relative py-20 md:py-32 overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle, #8B7355 1px, transparent 1px)',
            backgroundSize: '50px 50px'
          }}></div>
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            className="max-w-4xl mx-auto text-center"
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
          >
            <motion.h1
              className="text-5xl md:text-6xl lg:text-7xl font-serif text-navy-900 mb-6 leading-tight"
              variants={fadeInUp}
            >
              Get In <span className="text-primary-brown">Touch</span>
            </motion.h1>

            <motion.div
              className="w-24 h-1 bg-primary-brown mx-auto mb-8"
              variants={fadeInUp}
            ></motion.div>

            <motion.p
              className="text-xl md:text-2xl text-primary-dark-brown leading-relaxed font-light"
              variants={fadeInUp}
            >
              Let's discuss how we can bring your vision to life with quality finishes that last.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Contact Information Cards */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            variants={staggerContainer}
          >
            {contactInfo.map((info, index) => {
              const Icon = info.icon;
              return (
                <motion.div
                  key={index}
                  className="bg-accent-cream rounded-xl p-6 text-center hover:shadow-xl transition-shadow duration-300"
                  variants={fadeInUp}
                >
                  <div className="w-16 h-16 bg-primary-brown rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-serif text-navy-900 mb-3">
                    {info.title}
                  </h3>
                  <div className="space-y-1">
                    {info.details.map((detail, idx) => (
                      info.link && idx === 0 ? (
                        <a
                          key={idx}
                          href={info.link}
                          className="block text-primary-dark-brown hover:text-primary-brown transition-colors"
                        >
                          {detail}
                        </a>
                      ) : (
                        <p key={idx} className="text-primary-dark-brown text-sm">
                          {detail}
                        </p>
                      )
                    ))}
                  </div>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      
    </div>
  );
};

export default Contact;
